import { ReactNode } from 'react';
import { Icon } from '@iconify-icon/react';
import Link from 'next/link';

interface DocsCardGridProps {
  /** Columns at the `sm` breakpoint and up. Single column on mobile. */
  columns?: 2 | 3 | 4;
  children: ReactNode;
}

const COLUMN_CLASS: Record<2 | 3 | 4, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
};

export const DocsCardGrid: React.FC<DocsCardGridProps> = ({ columns = 2, children }) => (
  <div className={`not-prose my-6 grid grid-cols-1 gap-3 ${COLUMN_CLASS[columns]}`}>{children}</div>
);

interface DocsCardProps {
  icon?: string;
  title: string;
  /** When set the whole card becomes a link. */
  href?: string;
  children?: ReactNode;
}

/**
 * Small labelled tile. Used for feature grids, "supported formats", and
 * cross-links between articles.
 */
export const DocsCard: React.FC<DocsCardProps> = ({ icon, title, href, children }) => {
  const inner = (
    <>
      <div className="flex items-center gap-2.5">
        {icon && (
          <Icon icon={icon} className="flex-shrink-0 text-lg text-primary-600" aria-hidden="true" />
        )}
        <h4 className="!my-0 text-sm font-semibold text-slate-900">{title}</h4>
      </div>
      {children && (
        <p className="!mb-0 mt-1.5 text-sm leading-relaxed text-slate-600">{children}</p>
      )}
    </>
  );

  const base =
    'block h-full rounded-xl border border-slate-200 bg-white p-4 transition-all duration-150';

  if (href) {
    return (
      <Link href={href} className={`${base} hover:border-primary-300 hover:shadow-sm`}>
        {inner}
      </Link>
    );
  }

  return <div className={base}>{inner}</div>;
};

interface DocsTableProps {
  headers: string[];
  rows: ReactNode[][];
  /** Renders the first column in a monospace face — good for field/param tables. */
  monoFirstColumn?: boolean;
}

/**
 * Bordered, horizontally scrollable table.
 *
 * The prose plugin's default table has no border radius, no zebra, and — the
 * real problem — no scroll container, so a wide parameter table pushed the
 * whole page sideways on mobile.
 */
export const DocsTable: React.FC<DocsTableProps> = ({ headers, rows, monoFirstColumn }) => (
  <div className="not-prose my-6 overflow-hidden rounded-xl border border-slate-200">
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-slate-50">
            {headers.map((header) => (
              <th
                key={header}
                scope="col"
                className="whitespace-nowrap border-b border-slate-200 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            // Rows are static content authored in the article; index keys are stable.
            // eslint-disable-next-line react/no-array-index-key
            <tr key={rowIndex} className="border-b border-slate-100 last:border-b-0">
              {row.map((cell, cellIndex) => (
                <td
                  // eslint-disable-next-line react/no-array-index-key
                  key={cellIndex}
                  className={`px-4 py-3 align-top text-slate-700 ${
                    monoFirstColumn && cellIndex === 0
                      ? 'whitespace-nowrap font-mono text-[13px] text-slate-900'
                      : ''
                  }`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

const METHOD_STYLES: Record<HttpMethod, string> = {
  GET: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  POST: 'bg-primary-50 text-primary-700 border-primary-200',
  PATCH: 'bg-amber-50 text-amber-700 border-amber-200',
  PUT: 'bg-amber-50 text-amber-700 border-amber-200',
  DELETE: 'bg-rose-50 text-rose-700 border-rose-200',
};

interface DocsEndpointProps {
  method: HttpMethod;
  path: string;
  /** Scope required by `ApiKeyAccessGuard`, e.g. "bot:read". */
  scope?: string;
  children?: ReactNode;
}

/**
 * One REST endpoint header: coloured method chip, monospace path, and the
 * API-key scope it requires. Scope is shown because it is the single most
 * common reason a correct-looking request 403s.
 */
export const DocsEndpoint: React.FC<DocsEndpointProps> = ({ method, path, scope, children }) => (
  <div className="not-prose my-6 overflow-hidden rounded-xl border border-slate-200">
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
      <span
        className={`rounded-md border px-2 py-0.5 font-mono text-xs font-bold ${METHOD_STYLES[method]}`}>
        {method}
      </span>
      <code className="min-w-0 break-all font-mono text-sm text-slate-900">{path}</code>
      {scope && (
        <span className="ml-auto rounded-md bg-white px-2 py-0.5 font-mono text-[11px] text-slate-500 ring-1 ring-slate-200">
          {scope}
        </span>
      )}
    </div>
    {children && <div className="px-4 py-3 text-sm leading-relaxed text-slate-600">{children}</div>}
  </div>
);
